/* ==========================================================================
   Buena Vita — interaction layer

   Two jobs:
     1. MOTION   — one IntersectionObserver drives every reveal on the page.
     2. FEEDBACK — every add-to-cart becomes an AJAX call with a real reply:
                   button state, flying thumbnail, toast, free-shipping meter,
                   cross-sell, live cart count, and a readable error.

   Dependency-free on purpose. This theme's own JS (Alpine, elxr-core) changes
   between versions and has already broken things here once; nothing below
   touches it or relies on it.

   CONFIG comes from window.BVFX_CONFIG, written by snippets/bv-fx.liquid. The
   snippet is rendered by several sections, so the assignment there is guarded
   with `||` and the whole file is guarded with __bvFxInit — a duplicated
   <script src> would otherwise execute twice and double-bind every form.

   ON THE CURRENT 422: every variant in this store is stocked only at a
   third-party Supliful location reporting 0, so /cart/add.js answers
   422 "…is already sold out." for everything. That is a fulfilment problem,
   not a bug here. The error path below is written to surface Shopify's own
   message verbatim rather than inventing a friendlier one, because a shopper
   being told something vague about a real stock failure is worse than being
   told the truth.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__bvFxInit) return;
  window.__bvFxInit = true;

  var CFG = window.BVFX_CONFIG || {};
  var root = document.documentElement;
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var supportsIO = 'IntersectionObserver' in window;

  /* ========================================================================
     Utilities
     ===================================================================== */

  function $all(sel, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(sel));
  }

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  /* A compact port of Shopify's formatMoney. Handles the four money_format
     placeholder styles; anything unrecognised falls back to a plain dollar
     amount rather than printing the raw template string at the shopper. */
  function formatMoney(cents) {
    var fmt = CFG.moneyFormat || '${{amount}}';
    var value = (cents || 0) / 100;

    function withCommas(n, decimals) {
      var parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }

    function withDots(n, decimals) {
      var parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return parts.join(',');
    }

    var out = fmt
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withDots(value, 0))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withDots(value, 2))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommas(value, 0))
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas(value, 2));

    return /\{\{/.test(out) ? '$' + withCommas(value, 2) : out;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* Ask the CDN for roughly the size actually rendered, so the toast does not
     pull a 2048px file for a 46px thumb.

     Two URL styles reach this function: cart/add.js hands back a bare CDN path
     (…/file.png?v=123), while Liquid's image_url emits a modern ?width= URL.
     The legacy _NNNx filename form and ?width= must never be combined — the
     query param wins and the filename segment is then just noise — so a URL
     that already carries a width is returned untouched. */
  function sized(url, w) {
    if (!url) return '';
    if (/[?&]width=/.test(url)) return url;
    return url.replace(/(\.(?:png|jpe?g|webp|gif))(\?.*)?$/i, '_' + w + 'x$1$2');
  }

  /* ========================================================================
     1. MOTION
     ===================================================================== */

  var REVEAL_SEL = '.bv-reveal, .bv-fade, .bv-rise, .bv-slide-l, .bv-slide-r, .bv-pop, .bv-wipe';

  if (!reduced && supportsIO) root.classList.add('bv-anim');

  var revealIO = null;

  function initReveals(scope) {
    if (!root.classList.contains('bv-anim')) return;

    var targets = $all(REVEAL_SEL, scope).filter(function (n) {
      return !n.classList.contains('is-in');
    });
    if (!targets.length) return;

    if (!revealIO) {
      revealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-in');
          revealIO.unobserve(e.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    }

    targets.forEach(function (t) { revealIO.observe(t); });
  }

  /* Write the stagger index as a custom property so the delay lives in CSS and
     stays overridable per-section with --bv-step. */
  function initStagger(scope) {
    $all('[data-bv-stagger]', scope).forEach(function (parent) {
      if (parent.dataset.bvStaggered === '1') return;
      parent.dataset.bvStaggered = '1';

      var step = parent.getAttribute('data-bv-stagger');
      if (step) parent.style.setProperty('--bv-step', parseInt(step, 10) + 'ms');

      Array.prototype.forEach.call(parent.children, function (child, i) {
        child.style.setProperty('--bv-i', i);
      });
    });
  }

  /* Counters. data-bv-count holds the target number; the element's own text is
     used as a template so "4.7" and "100%" and "12" all animate correctly. */
  function initCounters(scope) {
    if (!supportsIO) return;

    var nodes = $all('[data-bv-count]', scope).filter(function (n) {
      return n.dataset.bvCounted !== '1';
    });
    if (!nodes.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        runCount(e.target);
      });
    }, { threshold: 0.4 });

    nodes.forEach(function (n) {
      n.dataset.bvCounted = '1';
      io.observe(n);
    });
  }

  function runCount(node) {
    var target = parseFloat(node.getAttribute('data-bv-count'));
    if (isNaN(target)) return;

    var decimals = (node.getAttribute('data-bv-count').split('.')[1] || '').length;
    var prefix = node.getAttribute('data-bv-prefix') || '';
    var suffix = node.getAttribute('data-bv-suffix') || '';

    if (reduced) {
      node.textContent = prefix + target.toFixed(decimals) + suffix;
      return;
    }

    var dur = parseInt(node.getAttribute('data-bv-dur') || '1100', 10);
    var start = null;

    function frame(ts) {
      if (start === null) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      node.textContent = prefix + (target * eased).toFixed(decimals) + suffix;
      if (p < 1) requestAnimationFrame(frame);
    }

    requestAnimationFrame(frame);
  }

  /* Magnetic hover. Skipped entirely on coarse pointers — on a touch screen it
     would only fire after a tap, which reads as the button drifting. */
  function initMagnetic(scope) {
    if (reduced) return;
    if (window.matchMedia && !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    $all('.bv-mag', scope).forEach(function (node) {
      if (node.dataset.bvMag === '1') return;
      node.dataset.bvMag = '1';

      var strength = parseFloat(node.getAttribute('data-bv-mag') || '0.28');

      node.addEventListener('mousemove', function (e) {
        var r = node.getBoundingClientRect();
        var dx = (e.clientX - (r.left + r.width / 2)) * strength;
        var dy = (e.clientY - (r.top + r.height / 2)) * strength;
        node.style.setProperty('--bv-mx', dx.toFixed(1) + 'px');
        node.style.setProperty('--bv-my', dy.toFixed(1) + 'px');
        node.classList.add('is-mag');
      });

      node.addEventListener('mouseleave', function () {
        node.classList.remove('is-mag');
        node.style.setProperty('--bv-mx', '0px');
        node.style.setProperty('--bv-my', '0px');
      });
    });
  }

  /* ========================================================================
     2. CART STATE
     ===================================================================== */

  var cart = { item_count: 0, total_price: 0, items: [] };

  function readCart() {
    return fetch(CFG.cartJsUrl || '/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (data) { cart = data; paintCart(); }
        return data;
      })
      .catch(function () { return null; });
  }

  function paintCart() {
    $all('[data-bv-cart-count]').forEach(function (n) {
      n.textContent = cart.item_count;
      n.setAttribute('data-count', cart.item_count);
    });

    $all('[data-bv-cart-total]').forEach(function (n) {
      n.textContent = formatMoney(cart.total_price);
    });

    /* Let anything else on the page — including the theme's own drawer, if it
       ever listens — know the cart moved. Nothing here depends on a listener
       existing. */
    document.dispatchEvent(new CustomEvent('bv:cart:updated', { detail: cart }));
  }

  function cartHasVariant(id) {
    return (cart.items || []).some(function (i) { return String(i.variant_id) === String(id); });
  }

  /* ========================================================================
     3. TOAST
     ===================================================================== */

  var toastHost = null;
  var toastNode = null;
  var toastTimer = null;

  function getHost() {
    if (toastHost && document.body.contains(toastHost)) return toastHost;
    toastHost = document.querySelector('.bv-toast-host');
    if (!toastHost) {
      toastHost = el('div', 'bv-toast-host bv-fx');
      toastHost.setAttribute('role', 'status');
      toastHost.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastHost);
    }
    return toastHost;
  }

  function closeToast() {
    if (!toastNode) return;
    var n = toastNode;
    toastNode = null;
    clearTimeout(toastTimer);
    n.classList.remove('is-open');
    setTimeout(function () { if (n.parentNode) n.parentNode.removeChild(n); }, 320);
  }

  var TICK_SVG =
    '<svg class="bv-atc__tick" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<path d="M4 12.5l5.2 5.2L20 7" stroke="currentColor" stroke-width="2.4" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var X_SVG =
    '<svg viewBox="0 0 12 12" fill="none" aria-hidden="true">' +
    '<path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.7" ' +
    'stroke-linecap="round"/></svg>';

  /* opts: { title, sub, image, error, dwell, showMeter, crossSell:[] } */
  function showToast(opts) {
    var host = getHost();
    closeToast();

    var node = el('div', 'bv-toast' + (opts.error ? ' is-error' : ''));

    var html =
      '<div class="bv-toast__row">' +
      (opts.image
        ? '<img class="bv-toast__thumb" src="' + escapeHtml(sized(opts.image, 92)) + '" alt="" width="46" height="46">'
        : '') +
      '<div class="bv-toast__txt">' +
      '<p class="bv-toast__title">' + escapeHtml(opts.title) + '</p>' +
      (opts.sub ? '<p class="bv-toast__sub">' + escapeHtml(opts.sub) + '</p>' : '') +
      '</div>' +
      '<button type="button" class="bv-toast__x" aria-label="Dismiss">' + X_SVG + '</button>' +
      '</div>';

    if (opts.showMeter) html += meterHtml();

    if (opts.crossSell && opts.crossSell.length) {
      html +=
        '<div class="bv-toast__xsell">' +
        '<p class="bv-toast__xsell-h">' + escapeHtml(CFG.crossSellHeading || 'Often taken with this') + '</p>' +
        opts.crossSell.map(crossSellRow).join('') +
        '</div>';
    }

    if (!opts.error) {
      html +=
        '<div class="bv-toast__acts">' +
        '<a class="bv-toast__btn" href="' + escapeHtml(CFG.cartUrl || '/cart') + '">View cart</a>' +
        '<a class="bv-toast__btn bv-toast__btn--go" href="/checkout">Checkout</a>' +
        '</div>' +
        '<div class="bv-toast__timer"><i></i></div>';
    } else if (CFG.contactUrl) {
      html +=
        '<div class="bv-toast__acts">' +
        '<a class="bv-toast__btn" href="' + escapeHtml(CFG.contactUrl) + '">Get help</a>' +
        '</div>';
    }

    node.innerHTML = html;
    host.appendChild(node);
    toastNode = node;

    node.querySelector('.bv-toast__x').addEventListener('click', closeToast);

    node.addEventListener('click', function (e) {
      var add = e.target.closest ? e.target.closest('.bv-xs__add') : null;
      if (!add) return;
      e.preventDefault();
      var vid = add.getAttribute('data-variant');
      if (!vid) return;
      add.disabled = true;
      add.textContent = 'Adding…';
      var body = new FormData();
      body.append('id', vid);
      body.append('quantity', '1');
      postAdd(body).then(function (res) {
        if (res.ok) {
          add.textContent = 'Added';
          readCart().then(function () { refreshMeter(node); });
        } else {
          add.disabled = false;
          add.textContent = 'Add';
          showToast({ title: 'Could not add that', sub: res.message, error: true });
        }
      });
    });

    /* Force a reflow so the opening transition actually runs — without it the
       browser may batch the class change with the insert and skip the frame. */
    void node.offsetWidth;
    node.classList.add('is-open');

    if (opts.showMeter) refreshMeter(node);

    var dwell = opts.dwell || (opts.error ? 9000 : 6500);
    var bar = node.querySelector('.bv-toast__timer i');

    if (bar && !reduced) {
      bar.style.animationDuration = dwell + 'ms';
      bar.classList.add('is-running');
    }

    function pause() { if (bar) bar.classList.add('is-paused'); clearTimeout(toastTimer); }
    function resume() {
      if (bar) bar.classList.remove('is-paused');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(closeToast, dwell);
    }

    node.addEventListener('mouseenter', pause);
    node.addEventListener('mouseleave', resume);
    node.addEventListener('focusin', pause);
    node.addEventListener('focusout', resume);

    toastTimer = setTimeout(closeToast, dwell);
    return node;
  }

  function crossSellRow(p) {
    return (
      '<div class="bv-xs">' +
      (p.image ? '<img class="bv-xs__img" src="' + escapeHtml(sized(p.image, 68)) + '" alt="" width="34" height="34">' : '') +
      '<div class="bv-xs__t">' + escapeHtml(p.title) + '<span>' + escapeHtml(formatMoney(p.price)) + '</span></div>' +
      '<button type="button" class="bv-xs__add" data-variant="' + escapeHtml(p.variant) + '">Add</button>' +
      '</div>'
    );
  }

  /* ---------- free-shipping meter ---------- */

  function meterHtml() {
    return (
      '<div class="bv-meter">' +
      '<p class="bv-meter__txt" data-bv-meter-txt></p>' +
      '<div class="bv-meter__track"><i class="bv-meter__fill" data-bv-meter-fill></i></div>' +
      '</div>'
    );
  }

  function refreshMeter(scope) {
    var threshold = parseInt(CFG.freeShipThreshold || 0, 10);
    var txt = (scope || document).querySelector('[data-bv-meter-txt]');
    var fill = (scope || document).querySelector('[data-bv-meter-fill]');
    if (!txt || !fill || !threshold) return;

    var total = cart.total_price || 0;
    var remaining = threshold - total;

    if (remaining <= 0) {
      txt.innerHTML = '<strong>Free shipping unlocked.</strong> Nice one.';
      fill.style.width = '100%';
      fill.classList.add('is-full');
    } else {
      /* v19: say what shipping costs until then. Both carts that were abandoned
         from ads were 3-bottle orders under the threshold that met the $8 only
         at checkout; this meter (toast + cart page) never mentioned it. */
      var rate = parseInt(CFG.shipRate || 0, 10);
      txt.innerHTML =
        '<strong>' + escapeHtml(formatMoney(remaining)) + '</strong> away from free shipping' +
        (rate > 0 ? '. Until then, shipping is ' + escapeHtml(formatMoney(rate)) + '.' : '');
      fill.style.width = Math.max(4, Math.round((total / threshold) * 100)) + '%';
      fill.classList.remove('is-full');
    }
  }

  /* ========================================================================
     4. FLYING THUMBNAIL
     ===================================================================== */

  function flyToCart(sourceImg) {
    if (reduced || !sourceImg) return;

    var target =
      document.querySelector('[data-bv-cart-target]') ||
      document.querySelector('#cart-icon-bubble') ||
      document.querySelector('a[href$="/cart"]');

    var from = sourceImg.getBoundingClientRect();
    if (!from.width) return;

    var to = target
      ? target.getBoundingClientRect()
      : { left: window.innerWidth - 60, top: 20, width: 30, height: 30 };

    var clone = document.createElement('img');
    clone.src = sourceImg.currentSrc || sourceImg.src;
    clone.className = 'bv-fly';
    clone.setAttribute('aria-hidden', 'true');
    clone.style.left = from.left + 'px';
    clone.style.top = from.top + 'px';
    clone.style.width = from.width + 'px';
    clone.style.height = from.height + 'px';
    clone.style.setProperty('--bv-dx', (to.left + to.width / 2 - (from.left + from.width / 2)) + 'px');
    clone.style.setProperty('--bv-dy', (to.top + to.height / 2 - (from.top + from.height / 2)) + 'px');

    document.body.appendChild(clone);

    clone.addEventListener('animationend', function () {
      if (clone.parentNode) clone.parentNode.removeChild(clone);
      if (target) {
        target.classList.add('bv-cart-bump');
        setTimeout(function () { target.classList.remove('bv-cart-bump'); }, 460);
      }
    });
  }

  /* ========================================================================
     5. ADD TO CART
     ===================================================================== */

  /* Shopify answers an add failure in two different shapes depending on which
     endpoint was hit:
       /cart/add.js  ->  { status, message, description }
       /cart/add     ->  { errors: "..." }  or  { errors: { field: [...] } }
     bv-fx.liquid points us at the .js endpoint, but reading both costs nothing
     and means a misconfigured cartAddUrl degrades to a real message instead of
     a shrug. */
  function errorMessage(data) {
    var generic = 'That could not be added.';
    if (!data) return generic;

    if (typeof data.description === 'string' && data.description) return data.description;
    if (typeof data.message === 'string' && data.message) return data.message;

    var errs = data.errors;
    if (typeof errs === 'string' && errs) return errs;

    if (errs && typeof errs === 'object') {
      var parts = [];
      Object.keys(errs).forEach(function (k) {
        parts.push([].concat(errs[k]).join(', '));
      });
      if (parts.length) return parts.join(' ');
    }

    return generic;
  }

  function postAdd(body) {
    return fetch(CFG.cartAddUrl || '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      body: body
    })
      .then(function (r) {
        /* Read as text and parse by hand. r.json() on an HTML error page
           rejects, which would land in the catch below and tell the shopper
           their connection failed when it plainly did not. */
        return r.text().then(function (raw) {
          var data = null;
          try { data = JSON.parse(raw); } catch (err) { data = null; }

          if (r.ok) return { ok: true, data: data || {} };
          return { ok: false, message: errorMessage(data) };
        });
      })
      .catch(function () {
        return { ok: false, message: 'Network problem — check your connection and try again.' };
      });
  }

  /* Give a plain <button> the three-state treatment. Width and height are
     locked from the resting measurement first, so swapping the label for a
     spinner cannot make the button jump. */
  function prepButton(btn) {
    if (btn.dataset.bvAtc === '1') return;
    btn.dataset.bvAtc = '1';

    var r = btn.getBoundingClientRect();
    if (r.width) {
      btn.style.minWidth = Math.ceil(r.width) + 'px';
      btn.style.minHeight = Math.ceil(r.height) + 'px';
    }

    var label = el('span', 'bv-atc__label');
    while (btn.firstChild) label.appendChild(btn.firstChild);

    btn.appendChild(label);
    btn.appendChild(el('span', 'bv-atc__state bv-atc__state--load', '<i class="bv-atc__spin"></i>'));
    btn.appendChild(el('span', 'bv-atc__state bv-atc__state--done', TICK_SVG));
    btn.classList.add('bv-atc', 'bv-press');
  }

  function setBtn(btn, state) {
    if (!btn) return;
    btn.classList.toggle('is-loading', state === 'loading');
    btn.classList.toggle('is-done', state === 'done');
    btn.disabled = state === 'loading';
    if (state === 'loading') btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
  }

  function showInlineError(form, message) {
    var box = form.querySelector('.bv-inline-err');
    if (!box) {
      box = el('p', 'bv-inline-err');
      box.setAttribute('role', 'alert');
      form.appendChild(box);
    }
    var help = CFG.contactUrl
      ? ' <a href="' + escapeHtml(CFG.contactUrl) + '">Tell us and we will sort it.</a>'
      : '';
    box.innerHTML = escapeHtml(message) + help;

    /* Re-trigger the shake if the same error fires twice. */
    box.style.animation = 'none';
    void box.offsetWidth;
    box.style.animation = '';
  }

  function clearInlineError(form) {
    var box = form.querySelector('.bv-inline-err');
    if (box && box.parentNode) box.parentNode.removeChild(box);
  }

  function handleSubmit(e) {
    var form = e.target;
    if (!form.matches || !form.matches('form[action*="/cart/add"]')) return;
    if (form.hasAttribute('data-bv-no-ajax')) return;

    e.preventDefault();

    /* The submitter may live outside the form via the HTML form= attribute —
       that is how the sticky bar's duplicate button works — so fall back to a
       document-wide lookup rather than assuming it is a descendant. */
    var btn =
      e.submitter ||
      form.querySelector('[type="submit"]') ||
      document.querySelector('[form="' + form.getAttribute('id') + '"][type="submit"]');

    if (btn) { prepButton(btn); setBtn(btn, 'loading'); }
    clearInlineError(form);

    var card = form.closest('.bv-card, .bv-ccard, .bv-buybox, .bv-pair__item');
    var img = card ? card.querySelector('img') : null;

    postAdd(new FormData(form)).then(function (res) {
      if (!res.ok) {
        setBtn(btn, 'idle');
        showInlineError(form, res.message);
        showToast({ title: 'Could not add to cart', sub: res.message, error: true });
        return;
      }

      setBtn(btn, 'done');
      flyToCart(img);

      if (card) {
        card.classList.add('bv-added-flash');
        setTimeout(function () { card.classList.remove('bv-added-flash'); }, 600);
      }

      readCart().then(function () {
        showToast({
          title: 'Added to your cart',
          sub: res.data.product_title || res.data.title,
          image: res.data.image || res.data.featured_image,
          showMeter: !!CFG.freeShipThreshold,
          crossSell: pickCrossSell(res.data.product_id)
        });
      });

      setTimeout(function () { setBtn(btn, 'idle'); }, 2200);
    });
  }

  /* Two products, chosen from the config list, excluding the one just added and
     anything already sitting in the cart. Suggesting something the shopper has
     already bought is the fastest way to make a recommendation feel automated. */
  function pickCrossSell(justAddedProductId) {
    var pool = CFG.crossSell || [];
    return pool
      .filter(function (p) {
        return String(p.id) !== String(justAddedProductId) && !cartHasVariant(p.variant);
      })
      .slice(0, 2);
  }

  /* ========================================================================
     5b. GALLERY ZOOM — magnifier + lightbox

     The magnifier moves transform-origin to the cursor and scales, so the
     image appears to be inspected rather than dragged. Only bound where hover
     is real; on touch the same element opens the lightbox instead, which is
     the only zoom that works with a finger.

     Image sources come from the thumbnails' data-full attribute (the gallery
     already stores a 1400px URL there for its own swapping). Falling back to
     the main image means a product with a single photo still gets a lightbox.
     ===================================================================== */

  var lb = null;
  var lbState = { urls: [], i: 0 };

  function buildLightbox() {
    if (lb) return lb;

    lb = el('div', 'bv-lb');
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Product image');
    lb.innerHTML =
      '<img class="bv-lb__img" alt="">' +
      '<button type="button" class="bv-lb__x" aria-label="Close">' + X_SVG + '</button>' +
      '<button type="button" class="bv-lb__nav bv-lb__nav--prev" aria-label="Previous image">' +
      '<svg viewBox="0 0 16 16" fill="none"><path d="M10 2L4 8l6 6" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
      '<button type="button" class="bv-lb__nav bv-lb__nav--next" aria-label="Next image">' +
      '<svg viewBox="0 0 16 16" fill="none"><path d="M6 2l6 6-6 6" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
      '<span class="bv-lb__count"></span>';

    document.body.appendChild(lb);

    lb.querySelector('.bv-lb__x').addEventListener('click', closeLightbox);
    lb.querySelector('.bv-lb__nav--prev').addEventListener('click', function (e) { e.stopPropagation(); stepLightbox(-1); });
    lb.querySelector('.bv-lb__nav--next').addEventListener('click', function (e) { e.stopPropagation(); stepLightbox(1); });

    /* Clicking the backdrop closes; clicking the photo itself does not. */
    lb.addEventListener('click', function (e) { if (e.target === lb) closeLightbox(); });

    return lb;
  }

  var lbLastFocus = null;

  function paintLightbox() {
    var img = lb.querySelector('.bv-lb__img');
    img.src = lbState.urls[lbState.i];

    var many = lbState.urls.length > 1;
    lb.querySelector('.bv-lb__nav--prev').hidden = !many;
    lb.querySelector('.bv-lb__nav--next').hidden = !many;
    lb.querySelector('.bv-lb__count').textContent = many
      ? lbState.i + 1 + ' / ' + lbState.urls.length
      : '';
  }

  function stepLightbox(dir) {
    if (!lbState.urls.length) return;
    lbState.i = (lbState.i + dir + lbState.urls.length) % lbState.urls.length;
    paintLightbox();
  }

  function openLightbox(urls, index) {
    if (!urls.length) return;
    buildLightbox();

    lbLastFocus = document.activeElement;
    lbState.urls = urls;
    lbState.i = index || 0;
    paintLightbox();

    void lb.offsetWidth;
    lb.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    lb.querySelector('.bv-lb__x').focus();
  }

  function closeLightbox() {
    if (!lb) return;
    lb.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(function () { if (lb && lb.parentNode) lb.parentNode.removeChild(lb); lb = null; }, 280);
    if (lbLastFocus && lbLastFocus.focus) lbLastFocus.focus();
  }

  function galleryUrls(scope) {
    var thumbs = $all('[data-bv-thumb]', scope || document);
    if (thumbs.length) {
      return thumbs.map(function (t) { return t.getAttribute('data-full'); }).filter(Boolean);
    }
    var main = (scope || document).querySelector('[data-bv-gallery-main] img');
    return main ? [main.currentSrc || main.src] : [];
  }

  function initZoom(scope) {
    var panel = (scope || document).querySelector('[data-bv-gallery-main]');
    if (!panel || panel.dataset.bvZoom === '1') return;
    panel.dataset.bvZoom = '1';
    panel.classList.add('bv-zoomable');

    var img = panel.querySelector('img');
    if (!img) return;

    var fine = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    if (fine && !reduced) {
      panel.addEventListener('mousemove', function (e) {
        var r = panel.getBoundingClientRect();
        var x = ((e.clientX - r.left) / r.width) * 100;
        var y = ((e.clientY - r.top) / r.height) * 100;
        var current = panel.querySelector('img');
        if (!current) return;
        current.style.transformOrigin = x + '% ' + y + '%';
        current.style.transform = 'scale(2)';
        panel.classList.add('is-zooming');
      });

      panel.addEventListener('mouseleave', function () {
        var current = panel.querySelector('img');
        if (current) { current.style.transform = ''; current.style.transformOrigin = ''; }
        panel.classList.remove('is-zooming');
      });
    }

    panel.addEventListener('click', function (e) {
      /* The gallery panel is not a link, but guard anyway in case it is ever
         wrapped in one. */
      e.preventDefault();
      var urls = galleryUrls(document);
      var current = panel.querySelector('img');
      var currentSrc = current ? (current.currentSrc || current.src) : '';

      /* Open on whichever image is currently showing. Thumbnail URLs are
         1400px wide while the displayed one may be any size, so match on the
         CDN filename rather than the full URL. */
      var name = currentSrc.split('?')[0].split('/').pop().replace(/_\d+x\d*\./, '.');
      var idx = urls.findIndex(function (u) {
        return u.split('?')[0].split('/').pop().replace(/_\d+x\d*\./, '.') === name;
      });

      openLightbox(urls, idx > -1 ? idx : 0);
    });
  }

  /* ========================================================================
     6. RECENTLY VIEWED
     ===================================================================== */

  var RV_KEY = 'bv:recently-viewed';

  function recordView() {
    if (!CFG.currentProduct) return;
    try {
      var list = JSON.parse(localStorage.getItem(RV_KEY) || '[]');
      list = list.filter(function (h) { return h !== CFG.currentProduct; });
      list.unshift(CFG.currentProduct);
      localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 8)));
    } catch (err) {
      /* Private mode or a full quota — recently-viewed is a nicety, never a
         reason to break the page. */
    }
  }

  function paintRecentlyViewed() {
    var hosts = $all('[data-bv-recent]');
    if (!hosts.length) return;

    var seen = [];
    try { seen = JSON.parse(localStorage.getItem(RV_KEY) || '[]'); } catch (err) { seen = []; }

    hosts.forEach(function (host) {
      var shown = 0;
      $all('[data-bv-handle]', host).forEach(function (item) {
        var handle = item.getAttribute('data-bv-handle');
        var keep = seen.indexOf(handle) !== -1 && handle !== CFG.currentProduct;
        item.hidden = !keep;
        if (keep) shown++;
      });
      host.hidden = shown === 0;
    });
  }

  /* ========================================================================
     Boot
     ===================================================================== */

  /* ========================================================================
     7. PROGRESSIVE ENHANCEMENT

     The homepage and product sections were built before this layer existed and
     carry only the original .bv-reveal class. Rather than editing eight Liquid
     files to sprinkle motion classes through markup that already works, the
     structures are recognised here and decorated at runtime.

     The trade is deliberate: the classes live in JS instead of the template, so
     someone reading bv-hero.liquid will not see them. That is why each rule
     below names the structure it targets. Anything genuinely new (bv-stack,
     bv-collection) puts its motion classes in its own markup where they belong;
     this function exists only for the sections that predate the layer.
     ===================================================================== */

  function enhance(scope) {
    var s = scope || document;

    /* Grids animate their children in sequence rather than as one block. */
    [
      ['.bv-products__grid', 55],
      ['.bv-goals__grid', 70],
      ['.bv-pillars__grid', 90],
      ['.bv-standards__grid', 70],
      ['.bv-strip__grid', 45]
    ].forEach(function (pair) {
      $all(pair[0], s).forEach(function (grid) {
        if (!grid.hasAttribute('data-bv-stagger')) grid.setAttribute('data-bv-stagger', pair[1]);
        Array.prototype.forEach.call(grid.children, function (child) {
          /* .bv-reveal is already the theme's own entrance; adding .bv-rise on
             top would double the transform. Only bare children get one. */
          if (!child.classList.contains('bv-reveal')) child.classList.add('bv-rise');
        });
      });
    });

    /* Primary calls to action get weight: a magnetic pull and a shine sweep.
       The product buy button and its sticky twin are included — they are the
       two most important controls on the site and were getting nothing. */
    $all('.bv__btn--primary, .bv-buy__submit, .bv-sticky__btn, .bv-sum__go, .bv-ccard__add, .bv-card__cta', s)
      .forEach(function (btn) {
        btn.classList.add('bv-press');

        /* THE THEME PAINTS OUR ADD-TO-CART BUTTON YELLOW (#e3e372).
           `enhanced-button-effects` tags every submit button with
           `.button-gradient-effect` and an inline !important rule sets
           `background: var(--global-button-bg-color)`. That selector is
           (0,2,0) — the same weight as `.bv .bv-buy__submit` — so the two
           were fighting on document order and the theme kept winning.

           Every one of those theme selectors ends in
           `:not(.disable-global-button-styling)`, which is the theme's own
           designed escape hatch. Adding that class removes our buttons from
           the match entirely, which is far more stable than escalating
           specificity forever. bv-type.css also carries a (0,3,0) rule so the
           colour is still right if this script never runs. */
        btn.classList.add('disable-global-button-styling');

        if (btn.classList.contains('bv__btn--primary') || btn.classList.contains('bv-buy__submit')) {
          btn.classList.add('bv-shine');
        }
      });

    /* Everything clickable gets press feedback — the cheapest possible signal
       that a tap registered, and the one most missing on touch. */
    $all('.bv__btn, .bv-chip, .bv-card__cta, .bv-faq__q, .bv-buy__qtybtn, .bv-stack__chip, .bv-gal__thumb', s)
      .forEach(function (n) { n.classList.add('bv-press'); });

    /* Headings rise line by line. This replaced an earlier .bv-wipe rule that
       was guarded with `!h.closest('.bv-reveal')` — and since every
       .bv__section-head IS a .bv-reveal, that guard meant the wipe never once
       applied. The line reveal composes with an ancestor reveal instead of
       fighting it, so no guard is needed. */
    $all('.bv-hero .bv__h1, .bv__section-head .bv__h2, .bv-buy__name', s).forEach(function (h) {
      h.setAttribute('data-bv-lines', '');
    });

    /* Product imagery settles as it enters, where the browser supports
       scroll-linked timelines. Purely decorative; nothing depends on it.

       The product gallery is deliberately EXCLUDED. Its main image is swapped
       by bv-product.js when a thumbnail is clicked, and a scroll-linked scale
       running on the element being replaced makes the swap look like a glitch. */
    $all('.bv-card__media img, .bv-ccard__media img:first-child, .bv-hero__media img', s)
      .forEach(function (img) { img.classList.add('bv-settle'); });
  }

  /* ========================================================================
     8. LINE-BY-LINE HEADING REVEAL

     Each VISUAL line gets an overflow-hidden clipper and an inner mover, so the
     text rises out of the line above rather than fading in place.

     Lines can only be found after layout, which is why this is JS and not
     Liquid: the split has to survive whatever the browser actually did with the
     wrap. Words are measured by offsetTop and grouped, then the element is
     rebuilt from those groups.

     Three things this gets right that a naive splitter does not:
       - it waits for document.fonts.ready, because measuring line breaks in the
         fallback face and then swapping to Inter regroups every line;
       - it refuses to touch a heading containing elements, since rebuilding
         innerHTML would destroy any link or span inside it;
       - it re-splits on WIDTH change only, so a mobile URL bar collapsing (a
         height change) does not rebuild every heading on the page.
     ===================================================================== */

  var splitTargets = [];

  function splitLines(node) {
    /* Element children mean markup we would destroy. Leave it alone. */
    if (node.children.length && node.dataset.bvSplit !== '1') return;

    var original = node.dataset.bvOriginal || node.textContent;
    if (!original || !original.trim()) return;

    node.dataset.bvOriginal = original;

    var words = original.trim().split(/\s+/);
    if (words.length < 2) return;

    /* Pass one: every word as an inline-block so offsetTop is measurable. */
    node.textContent = '';
    words.forEach(function (w, i) {
      var span = document.createElement('span');
      span.textContent = w;
      span.style.display = 'inline-block';
      node.appendChild(span);
      if (i < words.length - 1) node.appendChild(document.createTextNode(' '));
    });

    /* Pass two: group by vertical position. The 2px tolerance absorbs
       sub-pixel differences between words on the same line. */
    var lines = [];
    var lastTop = null;

    Array.prototype.forEach.call(node.querySelectorAll('span'), function (span) {
      var top = span.offsetTop;
      if (lastTop === null || Math.abs(top - lastTop) > 2) {
        lines.push([]);
        lastTop = top;
      }
      lines[lines.length - 1].push(span.textContent);
    });

    if (!lines.length) { node.textContent = original; return; }

    /* Joined with a space, not an empty string. The line wrappers are block
       elements so the whitespace never renders — but without it textContent
       runs the lines together ("Dental + Oral HealthChewables"), which breaks
       copy-paste and the accessible name. */
    node.innerHTML = lines
      .map(function (lineWords, i) {
        return (
          '<span class="bv-line"><i style="--bv-line-i:' + i + '">' +
          escapeHtml(lineWords.join(' ')) +
          '</i></span>'
        );
      })
      .join(' ');

    /* Belt and braces for assistive tech: name the element from the original
       string so it can never be assembled from the fragments at all. */
    node.setAttribute('aria-label', original.trim());

    node.dataset.bvSplit = '1';

    if (splitTargets.indexOf(node) === -1) splitTargets.push(node);
  }

  function initLines(scope) {
    if (!root.classList.contains('bv-anim')) return;

    var nodes = $all('[data-bv-lines]', scope).filter(function (n) {
      return n.dataset.bvSplit !== '1';
    });
    if (!nodes.length) return;

    function run() {
      nodes.forEach(function (n) {
        splitLines(n);

        /* Drive the reveal from the heading itself. If an ancestor .bv-reveal
           also lands .is-in that is harmless — the CSS matches either. */
        if (supportsIO) {
          var io = new IntersectionObserver(function (entries) {
            entries.forEach(function (e) {
              if (!e.isIntersecting) return;
              e.target.classList.add('is-in');
              io.unobserve(e.target);
            });
          }, { rootMargin: '0px 0px -6% 0px', threshold: 0.15 });
          io.observe(n);
        } else {
          n.classList.add('is-in');
        }
      });
    }

    /* Measuring before the webfont lands regroups every line the moment Inter
       swaps in. document.fonts may be absent on old browsers — run anyway. */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
    else run();
  }

  var lastWidth = window.innerWidth;
  var resizeTimer = null;

  window.addEventListener('resize', function () {
    /* Width only. A height-only change is the mobile URL bar, and rebuilding
       every heading for that is both pointless and visibly janky. */
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;

    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      splitTargets.forEach(function (n) {
        n.dataset.bvSplit = '';
        n.textContent = n.dataset.bvOriginal || n.textContent;
        splitLines(n);
        n.classList.add('is-in');
      });
    }, 200);
  });

  function init(scope) {
    enhance(scope);
    initReveals(scope);
    initStagger(scope);
    initCounters(scope);
    initMagnetic(scope);
    initLines(scope);
    initZoom(scope);
    $all('form[action*="/cart/add"] [type="submit"]', scope).forEach(prepButton);
  }

  /* The theme's own CTAs — the contact form's Send, the cart drawer's checkout
     and add buttons — are painted with the merchant's global button colour, a
     yellow that appears nowhere else in this brand. The rule doing it is

       button[type="submit"]:not(.disable-global-button-styling):not(.button)
         :not([class*="klaviyo"])… { background-color: … !important }

     whose specificity is well past anything worth matching in a stylesheet —
     chasing it would mean stacking six classes and would break again the next
     time the theme adds a :not(). Every one of those selectors excludes
     `.disable-global-button-styling`, so opting the buttons out is both the
     stable fix and the one the theme intends. bv-type.css then supplies the
     brand colour at (0,4,0), which is enough once the global rule stops
     matching.

     Scoped to a named list on purpose: this must not become a blanket
     "de-theme every button on the site". */
  var THEME_CTAS = '.contact-form__button, .cart__checkout-button, .product-form__submit';

  function optOutThemeCtas(scope) {
    var nodes;
    try { nodes = (scope || document).querySelectorAll(THEME_CTAS); } catch (err) { return; }
    [].slice.call(nodes).forEach(function (btn) {
      btn.classList.add('disable-global-button-styling');
    });
  }

  function boot() {
    init(document);
    optOutThemeCtas(document);
    recordView();
    paintRecentlyViewed();
    readCart();

    /* One delegated listener on the document catches every product form on the
       page, including any the theme injects later. */
    document.addEventListener('submit', handleSubmit, true);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        if (lb) closeLightbox();
        else if (toastNode) closeToast();
        return;
      }
      /* Arrow keys page the lightbox, but only while it is the thing on
         screen — otherwise they would hijack normal page scrolling. */
      if (!lb) return;
      if (e.key === 'ArrowLeft') stepLightbox(-1);
      if (e.key === 'ArrowRight') stepLightbox(1);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* The theme editor swaps section markup without a page load. Re-init, then
     force anything already on screen into its final state — a freshly inserted
     section is usually in view, and the observer would otherwise leave it
     hidden until the merchant scrolls. */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
    $all(REVEAL_SEL, e.target).forEach(function (n) { n.classList.add('is-in'); });
    paintRecentlyViewed();
  });

  /* Add several variants in ONE request. /cart/add.js accepts a JSON body of
     { items: [{ id, quantity }, ...] }, which is atomic — either the whole
     basket lands or none of it does. Looping one fetch per item would leave a
     half-added stack behind the first failure, which is worse than failing
     cleanly. */
  function addItems(items) {
    return fetch(CFG.cartAddUrl || '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        return r.text().then(function (raw) {
          var data = null;
          try { data = JSON.parse(raw); } catch (err) { data = null; }
          if (r.ok) return { ok: true, data: data || {} };
          return { ok: false, message: errorMessage(data) };
        });
      })
      .then(function (res) {
        return res.ok ? readCart().then(function () { return res; }) : res;
      })
      .catch(function () {
        return { ok: false, message: 'Network problem — check your connection and try again.' };
      });
  }

  window.BVFX = {
    toast: showToast,
    closeToast: closeToast,
    cart: function () { return cart; },
    refresh: readCart,
    money: formatMoney,
    addItems: addItems,
    meterHtml: meterHtml,
    refreshMeter: refreshMeter,
    threshold: function () { return parseInt(CFG.freeShipThreshold || 0, 10); }
  };
})();
